"use client";

import { useContracts } from "@/lib/contract";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { parseUnits } from "viem";
import { usePublicClient } from "wagmi";

// Replace with the user's real available balance
const UNSTAKE_FEE_PCT = 1.5;

const PERCENT_OPTIONS = [25, 50, 75, 100];

function formatAmount(n: number) {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function shortenAddress(address: string | undefined) {
  if (!address) return "";
  return `${address.slice(0, 5)}...${address.slice(-5)}`;
}


export default function UnstakeCard({ data, onSuccess, address }: { data: { unstackAmount: string }; onSuccess: () => void; address: string | undefined }) {
  const PAYOUT_ADDRESS = shortenAddress(address) || "";
  const contractAddress = useContracts().stakingContract?.address;
  const AVAILABLE_BALANCE = parseFloat(data.unstackAmount) || 0;
  const [amount, setAmount] = useState<string>("");
  const [activePercent, setActivePercent] = useState<number | null>(null);
  const { stakingContract } = useContracts();
  const publicClient = usePublicClient();

  const [showConfirm, setShowConfirm] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [loading, setLoading] = useState(false);
  const [successReceiveAmount, setSuccessReceiveAmount] = useState(0);

  const numericAmount = useMemo(() => {
    const parsed = parseFloat(amount.replace(",", "."));
    return isNaN(parsed) ? 0 : parsed;
  }, [amount]);

  const sliderValue = useMemo(() => {
    if (AVAILABLE_BALANCE === 0) return 0;
    return Math.min(100, (numericAmount / AVAILABLE_BALANCE) * 100);
  }, [numericAmount]);

  const fee = numericAmount * (UNSTAKE_FEE_PCT / 100);
  const receiveAmount = Math.max(numericAmount - fee, 0);
  const hasAmount = numericAmount > 0;

  function applyAmount(value: number) {
    setAmount(value === 0 ? "" : value.toFixed(2));
  }

  function handlePercentClick(pct: number) {
    setActivePercent(pct);
    applyAmount((AVAILABLE_BALANCE * pct) / 100);
  }

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    const pct = Number(e.target.value);
    setActivePercent(null);
    applyAmount((AVAILABLE_BALANCE * pct) / 100);
  }

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    setActivePercent(null);

    const value = e.target.value;

    // Allow only numbers with optional decimal separator
    if (!/^[0-9]*[.,]?[0-9]*$/.test(value)) return;

    // Allow empty input
    if (value === "") {
      setAmount("");
      return;
    }

    const numericValue = parseFloat(value.replace(",", "."));

    // Prevent entering more than the available balance
    if (!isNaN(numericValue) && numericValue > AVAILABLE_BALANCE) {
      setAmount(AVAILABLE_BALANCE.toFixed(2));
      return;
    }

    setAmount(value);
  }

  function handleMax() {
    setActivePercent(100);
    applyAmount(AVAILABLE_BALANCE);
  }


  const handleSubmit = async () => {

    if (!stakingContract) {
      return;
    }
    try {
      setLoading(true);

      const amountInWei = parseUnits(amount, 18);

      const hash = await stakingContract.write.unstakeTokens([
        amountInWei.toString(),
      ]);

      setTxHash(hash);

      const receipt = await publicClient?.waitForTransactionReceipt({
        hash,
      });

      if (receipt?.status === "reverted") {
        toast.error("Unstake fehlgeschlagen.");
        return;
      }

      setTimeout(async () => {
        await onSuccess();
      }, 1500);

setSuccessReceiveAmount(receiveAmount);

      // Close confirmation modal
      setShowConfirm(false);

      // Open success modal
      setShowSuccess(true);

      setAmount("");
      setActivePercent(null);
    } catch (e) {
      toast.error("Unstake fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className=" bg-black flex items-center justify-center ">
      <div className="w-full rounded-3xl bg-[#111113] border border-white/5 p-6 sm:p-7 shadow-2xl">
        {/* Eyebrow */}
        <h1 className="text-gray-400 text-xs font-bold tracking-[0.25em] uppercase mb-4">
          Unstake
        </h1>

        {/* Amount input card */}
        <div className="rounded-2xl bg-[#0a0a0a] border border-white/5 px-5 py-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm">Betrag</span>
            <button
              onClick={handleMax}
              className="text-red-500 text-xs font-bold tracking-wider border border-red-500/40 rounded-lg px-3 py-1.5 hover:bg-red-500/10 transition-colors"
            >
              MAX
            </button>
          </div>
          <div className="flex items-center justify-between">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
              className="bg-transparent text-4xl font-mono text-gray-200 placeholder-gray-700 w-2/3 outline-none tracking-wide"
            />
            <span className="text-red-500 font-mono text-lg font-semibold">
              BCT
            </span>
          </div>
        </div>

        {/* Slider */}
        <div className="mb-5 px-1">
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={sliderValue}
            onChange={handleSliderChange}
            className="unstake-slider w-full"
            style={{
              background: `linear-gradient(to right, #ef4444 ${sliderValue}%, #2a2a2a ${sliderValue}%)`,
            }}
          />
        </div>

        {/* Percent buttons */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {PERCENT_OPTIONS.map((pct) => (
            <button
              key={pct}
              onClick={() => handlePercentClick(pct)}
              className={`rounded-xl border py-2.5 text-sm transition-colors ${activePercent === pct
                ? "border-red-500 text-red-500 bg-red-500/10"
                : "border-white/10 text-gray-300 hover:border-white/20"
                }`}
            >
              {pct}%
            </button>
          ))}
        </div>

        <div className="h-px bg-white/5 mb-6" />

        {/* Summary */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Unstake-Betrag</span>
            <span className="font-mono text-gray-200">
              {formatAmount(numericAmount)} BCT
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Unstaking-Gebühr ({UNSTAKE_FEE_PCT}%)
            </span>
            <span className="font-mono text-gray-400">
              -{formatAmount(fee)} BCT
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Du erhältst</span>
            <span className="font-mono text-white font-semibold">
              {formatAmount(receiveAmount)} BCT
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Auszahlung an</span>
            <span className="font-mono text-gray-400">{PAYOUT_ADDRESS}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Bearbeitungszeit</span>
            <span className="font-mono text-gray-400">sofort</span>
          </div>
        </div>

        {/* CTA */}
        <button
          disabled={!hasAmount}

          onClick={() => setShowConfirm(true)}
          className={`w-full rounded-2xl py-4 text-base font-bold transition-colors ${hasAmount
            ? "bg-red-600 text-white hover:bg-red-500"
            : "bg-white/5 text-gray-500 cursor-not-allowed"
            }`}
        >
          {hasAmount ? "Unstake bestätigen" : "Betrag eingeben"}
        </button>

        {/* Disclaimer */}
        <p className="text-center text-gray-500 text-xs leading-relaxed mt-5 px-2">
          Auf den Unstake-Betrag fällt eine Gebühr von {UNSTAKE_FEE_PCT}% an.
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
      </div>


      {
        showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#111113] shadow-2xl">

              {/* Header */}
              <div className="px-6 pt-6">
                <h2 className="text-3xl font-bold text-white">
                  Unstake bestätigen
                </h2>

                <p className="mt-2 text-base text-zinc-400 leading-7">
                  Bitte prüfe die Angaben. Nach der Bestätigung wird der Betrag
                  sofort an deine Wallet ausgezahlt.
                </p>
              </div>

              {/* Amount */}
              <div className="mx-6 mt-6 rounded-2xl border border-white/5 bg-[#18181c] py-6">
                <div className="flex items-end justify-center gap-2">
                  <span className="font-mono text-2xl md:text-4xl font-semibold text-white">
                    {formatAmount(numericAmount)}
                  </span>

                  <span className="pb-1 font-mono text-xl font-bold text-red-500">
                    BCT
                  </span>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-6 px-6 space-y-4 text-sm">

                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">
                    Von Vertrag
                  </span>

                  <span className="font-mono text-zinc-300">
                    {contractAddress ? shortenAddress(contractAddress) : "N/A"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">
                    An Wallet
                  </span>

                  <span className="font-mono text-zinc-300">
                    {PAYOUT_ADDRESS}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">
                    Unstaking-Gebühr ({UNSTAKE_FEE_PCT}%)
                  </span>

                  <span className="font-mono text-zinc-300">
                    -{formatAmount(fee)} BCT
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">
                    Du erhältst
                  </span>

                  <span className="font-mono font-semibold text-white">
                    {formatAmount(receiveAmount)} BCT
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">
                    Netzwerk
                  </span>

                  <span className="font-mono text-zinc-300">
                    BlockChain Mainnet
                  </span>
                </div>

              </div>

              {/* Buttons */}
              <div className="mt-8 grid grid-cols-2 gap-4 p-6">

                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={loading}
                  className="rounded-xl border border-white/10 py-3 text-base font-semibold text-zinc-300 transition hover:bg-white/5"
                >
                  Abbrechen
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="rounded-xl bg-red-600 py-3 text-base font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"
                >
                  {loading ? "Wird verarbeitet..." : "Jetzt unstaken"}
                </button>

              </div>

            </div>
          </div>
        )
      }

      {
        showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#111113] shadow-2xl">

              {/* Success Icon */}
              <div className="flex justify-center pt-8">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10">
                  <svg
                    className="h-10 w-10 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Heading */}
              <div className="px-6">
                <h2 className="mt-6 text-center text-3xl font-bold text-white">
                  Unstake erfolgreich
                </h2>

                <p className="mx-auto mt-3 max-w-md text-center text-base leading-7 text-zinc-400">
                  <span className="font-semibold text-white">
                    {formatAmount(successReceiveAmount)} BCT
                  </span>{" "}
                  wurden erfolgreich an deine Wallet{" "}
                  <span className="font-mono text-zinc-300">
                    {PAYOUT_ADDRESS}
                  </span>{" "}
                  ausgezahlt.
                </p>
              </div>

              {/* Details */}
              <div className="mx-6 mt-6 rounded-2xl border border-white/5 bg-[#18181c] p-5 space-y-4">

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">
                    Erhaltener Betrag
                  </span>

                  <span className="font-mono font-semibold text-white">
                    {formatAmount(successReceiveAmount)} BCT
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">
                    Wallet
                  </span>

                  <span className="font-mono text-zinc-300">
                    {PAYOUT_ADDRESS}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">
                    Netzwerk
                  </span>

                  <span className="font-mono text-zinc-300">
                    BlockChain Mainnet
                  </span>
                </div>

                <div className="border-t border-white/5 pt-4 flex items-center justify-between text-sm">
                  <span className="text-zinc-500">
                    Transaktion
                  </span>

                  <span className="font-mono text-zinc-300">
                    {shortenAddress(txHash)}
                  </span>
                </div>

              </div>

              {/* Button */}
              <div className="p-6">
                <button
                  onClick={() => setShowSuccess(false)}
                  className="w-full rounded-xl bg-red-600 py-3 text-base font-semibold text-white transition hover:bg-red-500"
                >
                  Fertig
                </button>
              </div>

            </div>
          </div>
        )
      }

      {/* Slider thumb styling (global, scoped via className) */}
      <style jsx global>{`
        .unstake-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 9999px;
          outline: none;
        }
        .unstake-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: #ffffff;
          border: 4px solid #ef4444;
          cursor: pointer;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
        }
        .unstake-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 9999px;
          background: #ffffff;
          border: 4px solid #ef4444;
          cursor: pointer;
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
        }
      `}</style>
    </div>
  );
}